import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Avatar } from "@/components/Avatar";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Star } from "lucide-react";

export default function DMs() {
  const nav = useNavigate();
  const [dms, setDms] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, f] = await Promise.all([api.get("/dms"), api.get("/friends")]);
      setDms(d.data);
      setFriends(f.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startDm = async (userId) => {
    try {
      const { data } = await api.post(`/dms/${userId}`);
      nav(`/room/${data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => nav("/lobby")}
          data-testid="dms-back-btn"
          className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Back to lobby
        </button>

        <div className="text-xs tracking-[0.25em] uppercase text-fuchsia-400 font-semibold">
          The line
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
          private notes.
        </h1>
        <p className="text-zinc-400 mt-3 max-w-xl">
          1-on-1 conversations with the people you've made a connection with.
        </p>

        {loading ? (
          <div className="text-zinc-500 mt-10">Loading…</div>
        ) : (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Threads */}
            <section className="lg:col-span-2">
              <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2 mb-4">
                <MessageCircle className="w-4 h-4 text-fuchsia-400" /> Threads
              </h2>
              {dms.length === 0 ? (
                <div className="rounded-3xl border border-white/5 bg-[#0c0c0f] p-10 text-center">
                  <div className="text-zinc-300 font-display font-bold">No DMs yet</div>
                  <div className="text-sm text-zinc-500 mt-1">
                    Tap a username in chat → "Message" to slide into someone's DMs.
                  </div>
                </div>
              ) : (
                <div className="rounded-3xl border border-white/5 bg-[#0c0c0f] divide-y divide-white/5 overflow-hidden">
                  {dms.map((d) => {
                    const partner = d.partner || {};
                    const lm = d.last_message;
                    const preview = lm
                      ? (lm.kind === "image"
                        ? "📷 Photo"
                        : (lm.content || "").slice(0, 80))
                      : "Say hi…";
                    return (
                      <button
                        key={d.id}
                        onClick={() => nav(`/room/${d.id}`)}
                        data-testid={`dm-thread-${d.id}`}
                        className="w-full flex items-center gap-4 p-4 hover:bg-white/[0.03] transition text-left"
                      >
                        <div className="relative">
                          <Avatar user={partner} size={48} />
                          {partner.is_online && (
                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0c0c0f]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-display font-bold truncate" data-testid={`dm-partner-${d.id}`}>
                              {partner.username || "Unknown"}
                            </span>
                            {lm?.created_at && (
                              <span className="text-[10px] text-zinc-500 shrink-0">
                                {new Date(lm.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-400 truncate mt-0.5">
                            {preview}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Favorites */}
            <aside>
              <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2 mb-4">
                <Star className="w-4 h-4 text-amber-400" /> Favorites
              </h2>
              {friends.length === 0 ? (
                <div className="rounded-3xl border border-white/5 bg-[#0c0c0f] p-6 text-sm text-zinc-500">
                  Star someone from their profile and they'll show up here.
                </div>
              ) : (
                <div className="rounded-3xl border border-white/5 bg-[#0c0c0f] divide-y divide-white/5 overflow-hidden">
                  {friends.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`friend-row-${u.id}`}
                      className="flex items-center gap-3 p-3"
                    >
                      <div className="relative">
                        <Avatar user={u} size={36} />
                        {u.is_online && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0c0c0f]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{u.username}</div>
                        <div className="text-[10px] text-zinc-500">
                          {u.is_online ? "online" : "offline"}
                        </div>
                      </div>
                      <button
                        onClick={() => startDm(u.id)}
                        data-testid={`dm-friend-${u.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-200 hover:bg-fuchsia-500/20 transition"
                      >
                        Message
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
